import { measurementReport, MEASUREMENT_TIMEOUT_MS } from '../../../../shared/hardware-measurement.js';
import type { HardwareCommand, HealthReport } from '../../../hardware-types';
import { measurementFrame, type MeasurementValues } from './measurementModel.ts';

export interface MeasurementSession {
  scope: string; command: HardwareCommand; startedAt: number | null;
  report: HealthReport | null; oxygen: HealthReport | null;
  failed: boolean; dismissed: boolean; terminal: boolean;
}
export function beginMeasurement(scope: string, command: HardwareCommand): MeasurementSession {
  return {scope,command,startedAt:null,report:null,oxygen:null,failed:false,dismissed:false,terminal:false};
}
export function measurementSessionFrame(session: MeasurementSession, now: number) {
  return measurementFrame({startedAt:session.startedAt,resultReady:session.report?.quality==='reported',
    timeoutAt:Date.parse(session.command.queuedAt)+MEASUREMENT_TIMEOUT_MS,
    failed:session.failed,cancelled:session.dismissed},now);
}
/** Reports come exclusively from the selected device's validated hardware snapshot.
 * The protocol has no request id: matching is temporal, not proof of causation. */
export function observeMeasurement(previous: MeasurementSession, scope: string, command: HardwareCommand,
  reports: HealthReport[], now: number): MeasurementSession {
  if(scope!==previous.scope || command.id!==previous.command.id || command.type!==previous.command.type)
    return beginMeasurement(scope,command);
  if(previous.dismissed) return previous;
  if(previous.terminal) {
    if(previous.failed || !previous.report || command.type!=='HRTSTART' || previous.oxygen) return previous;
    const oxygen=measurementReport(command,reports,now,'oxygen') as HealthReport | undefined;
    return oxygen?.quality==='reported' ? {...previous,oxygen} : previous;
  }
  const report=previous.report ?? measurementReport(command,reports,now) as HealthReport | undefined;
  const oxygen=previous.oxygen ?? (command.type==='HRTSTART'
    ? measurementReport(command,reports,now,'oxygen') as HealthReport | undefined : undefined);
  const ack=Date.parse(command.repliedAt??'');
  const startedAt=previous.startedAt ?? (Number.isFinite(ack) && ack<=now && ack>=Date.parse(command.queuedAt)
    ? ack : report?.quality==='reported' ? Date.parse(report.receivedAt) : null);
  const next: MeasurementSession={...previous,command,startedAt,report:report??null,
    oxygen:oxygen?.quality==='reported'?oxygen:null,
    failed:command.status==='failed'||Boolean(report&&report.quality!=='reported')};
  const phase=measurementSessionFrame(next,now).phase;
  return {...next,terminal:phase==='complete'||phase==='error',failed:next.failed||phase==='error'};
}
export function dismissMeasurement(session: MeasurementSession): MeasurementSession {
  return {...session,dismissed:true,terminal:true};
}
export function measurementSessionValues(session: MeasurementSession): MeasurementValues {
  const report=session.report?.quality==='reported'?session.report:null;
  return {pulse:report?.pulse??null,systolic:report?.systolic??null,diastolic:report?.diastolic??null,
    temperature:report?.temperatureC??null,oxygen:report?.oxygenPercent??session.oxygen?.oxygenPercent??null};
}
